import { Link } from "react-router-dom";
import { FourArrows } from "assets/website";
import {
  whoWeServe1,
  whoWeServe2,
  whoWeServe3,
  whoWeServe4,
} from "assets/images";
import { ChevronRight } from "lucide-react";

interface AudienceCard {
  title: string;
  subtitle: string;
  bg: string;
  image: string;
  link: string;
  imageAlt: string;
}

const row1: AudienceCard[] = [
  {
    title: "Builders",
    subtitle: "Founders hiring early teams",
    bg: "#FDEEED",
    image: whoWeServe1,
    link: "/use-cases?for=builders#precision-built-for",
    imageAlt: "Builders — founders hiring early teams",
  },
  {
    title: "Scalers",
    subtitle: "Growing companies scaling talent",
    bg: "#EDE8F8",
    image: whoWeServe2,
    link: "/use-cases?for=scalers#precision-built-for",
    imageAlt: "Scalers — growing companies scaling talent",
  },
];

const row2: AudienceCard[] = [
  {
    title: "Fixers",
    subtitle: "Leaders fixing underperforming teams",
    bg: "#FEF6E4",
    image: whoWeServe3,
    link: "/use-cases?for=fixers#precision-built-for",
    imageAlt: "Fixers — leaders fixing underperforming teams",
  },
  {
    title: "Assessors",
    subtitle: "Organizations seeking clarity on workforce capability",
    bg: "#E6F4F9",
    image: whoWeServe4,
    link: "/use-cases?for=assessors#precision-built-for",
    imageAlt: "Assessors — organizations seeking clarity on workforce capability",
  },
];

const Card = ({
  card,
  delay,
}: {
  card: AudienceCard;
  delay: number;
}) => (
  <div
    data-aos="fade-up"
    data-aos-delay={delay}
    className="relative rounded-xl overflow-hidden h-[280px] md:h-[340px]"
    style={{ backgroundColor: card.bg }}
  >
    {/* Text content — constrained to left half so image has room */}
    <div className={`absolute top-0 left-0 p-6 md:p-8 flex flex-col z-10 ${(card.title === 'Scalers' || card.title === 'Fixers') ? 'max-w-[90%]' : 'max-w-[60%]'} `}>
      <h3 className='text-lg md:text-xl lg:text-[28px] font-bold text-[#1D2939]'>
        {card.title}
      </h3>
      <p className="text-sm md:text-base lg:text-lg xl:text-2xl text-[#475467] mb-2">
        {card.subtitle}
      </p>
      <Link
        to={card.link}
        className="inline-flex items-center gap-1 text-[#175CD3] text-sm md:text-lg font-normal hover:gap-2 transition-all mt-1"
      >
        Learn More
        <ChevronRight className="w-5 h-5 text-[#175CD3] -mb-1" />
      </Link>
    </div>

    {/* Image anchored to bottom-right, fills ~55% width and full height */}
    <div className={`absolute bottom-0 right-0 h-full flex items-end justify-end ${card.title === 'Fixers' && '!w-[40%] rotate-80'} ${card.title === 'Scalers' && '-mr-[60px] -mb-[20px]'} w-[58%]`}>
      <img
        src={card.image}
        alt={card.imageAlt}
        className="w-full h-full object-contain object-right-bottom"
      />
    </div>
  </div>
);

const WhoWeServeSection = () => {
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container mx-auto xl:px-8 lg:px-4 px-4">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <div
            data-aos="fade-up"
            className="inline-flex py-1.5 items-center gap-2 mb-4 relative before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-[linear-gradient(90deg,#C6E0FF_0%,#000B55_100%)] after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[1px] after:bg-[linear-gradient(90deg,#C6E0FF_0%,#000B55_100%)]"
          >
            <FourArrows />
            <span className="sm:text-lg text-base text-[#050B17] tracking-wider">
              Who We Serve
            </span>
          </div>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-2xl md:text-4xl lg:text-5xl font-display font-bold text-[#050B17]"
          >
            Precision is Built For...
          </h2>
        </div>

        {/* Row 1: Builders (wide) | Scalers (narrow) — 3:2 split */}
        <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-4 mb-4">
          {row1.map((card, i) => (
            <Card key={card.title} card={card} delay={100 + i * 75} />
          ))}
        </div>

        {/* Row 2: Fixers (narrow) | Assessors (wide) — 2:3 split */}
        <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-4">
          {row2.map((card, i) => (
            <Card key={card.title} card={card} delay={250 + i * 75} />
          ))}
        </div>
      </div>
    </section>
  );
};

export { WhoWeServeSection };
